import { CheckCircle2, CircleDot, Circle, AlertTriangle } from 'lucide-react'
import { formatDate, formatCurrency } from '@/lib/utils'
import { FormationMilestone, FormationTaxRegistrationProgress, FormationEntity, MilestoneKey, MilestoneStatus } from '@/types'

export const MILESTONE_LABELS: Record<MilestoneKey, string> = {
  submitted: 'Order received',
  name_cleared: 'Name cleared',
  filed: 'Filed with the Secretary of State',
  certificate_issued: 'Certificate issued',
  ein_issued: 'EIN issued',
  documents_ready: 'Final company documents ready'
}

const STATUS_LABELS: Record<MilestoneStatus, string> = {
  done: 'Done',
  in_progress: 'In progress',
  waiting: 'Not started yet'
}

// The entity's own processing-day range is the only source an expected
// timeframe is ever allowed to come from — never a fixed claim. See the
// same phrase used at quote time in Wizard/States/Edit.tsx.
function filingEstimate(entity: FormationEntity): string | null {
  if (entity.processing_days_min == null || entity.processing_days_max == null) return null
  return `our filing estimate is ${entity.processing_days_min}-${entity.processing_days_max} business days`
}

// No milestone in this build ever needs founder action — every input it
// depends on was collected during the wizard, before payment. waitingOn is
// always Corppy or the state. The "rejected" branch below is the one place
// something needs unmissable attention today (role="alert", destructive
// tokens, text-only — never colour alone); reuse that same treatment if a
// milestone ever does need the founder to act.
type Description = { body: string; waitingOn: string | null }

export function describeMilestone(
  milestone: FormationMilestone,
  entity: FormationEntity,
  supersededByLaterProgress: boolean
): Description {
  const jurisdiction = entity.jurisdiction_code
  const estimate = filingEstimate(entity)

  switch (milestone.key) {
    case 'submitted':
      return milestone.done
        ? { body: 'Payment confirmed. Corppy is starting your formation.', waitingOn: null }
        : { body: 'Waiting for payment to be confirmed.', waitingOn: 'Payment' }

    case 'name_cleared':
      if (milestone.done) return { body: `"${milestone.cleared_name}" is cleared and locked in as your company name.`, waitingOn: null }
      return milestone.status === 'in_progress'
        ? { body: `We're confirming your company name with ${jurisdiction}.`, waitingOn: 'Corppy' }
        : { body: 'This is next, once your name is confirmed available.', waitingOn: 'Reaching this step' }

    case 'filed': {
      if (milestone.done) {
        const parts = [`Filed with ${jurisdiction} on ${formatDate(milestone.at || '')}.`]
        if (estimate && !supersededByLaterProgress) parts.push(`Once accepted, ${estimate} — not a guarantee.`)
        const stillAwaitingReview = !milestone.rejected && !supersededByLaterProgress
        return { body: parts.join(' '), waitingOn: stillAwaitingReview ? `${jurisdiction}'s review` : null }
      }
      return milestone.status === 'in_progress'
        ? { body: `We're preparing your filing with ${jurisdiction}.`, waitingOn: 'Corppy' }
        : { body: 'This happens once your name is cleared.', waitingOn: 'Reaching this step' }
    }

    case 'certificate_issued':
      if (milestone.done) {
        const seeItAbove = milestone.certificate_attached ? ' — see it above' : ''
        return { body: `Your certificate was issued on ${formatDate(milestone.at || '')}${seeItAbove}.`, waitingOn: null }
      }
      return milestone.status === 'in_progress'
        ? { body: `Waiting on ${jurisdiction} to finish reviewing your filing.${estimate ? ` For reference, ${estimate}.` : ''}`, waitingOn: `${jurisdiction}'s review` }
        : { body: 'This happens once you are filed.', waitingOn: 'Reaching this step' }

    case 'ein_issued':
      if (milestone.done) return { body: `Your EIN is ${milestone.reference_number || 'on file'}, issued ${formatDate(milestone.at || '')}.`, waitingOn: null }
      return milestone.status === 'in_progress'
        ? { body: "We'll apply for your EIN now that your certificate is issued.", waitingOn: 'Corppy' }
        : { body: 'This happens once your certificate is issued.', waitingOn: 'Reaching this step' }

    case 'documents_ready':
      if (milestone.done) return { body: 'Your filed company records and completed EIN materials are ready below.', waitingOn: null }
      return milestone.status === 'in_progress'
        ? { body: "We're assembling your final filed records and EIN materials.", waitingOn: 'Corppy' }
        : { body: 'Your reviewed application package is already on file. Final filed records follow after your EIN is issued.', waitingOn: 'Reaching this step' }
  }
}

function StatusIcon({ status }: { status: MilestoneStatus }) {
  if (status === 'done') return <CheckCircle2 className="w-4 h-4 text-green-600" aria-hidden="true" />
  if (status === 'in_progress') return <CircleDot className="w-4 h-4 text-blue-600" aria-hidden="true" />
  return <Circle className="w-4 h-4 text-muted-foreground/40" aria-hidden="true" />
}

const STATUS_TEXT_CLASS: Record<MilestoneStatus, string> = {
  done: 'text-green-600',
  in_progress: 'text-blue-600',
  waiting: 'text-muted-foreground'
}

type Props = {
  milestones: FormationMilestone[]
  entity: FormationEntity
  taxRegistrationProgress: FormationTaxRegistrationProgress[]
}

export function MilestoneList({ milestones, entity, taxRegistrationProgress }: Props) {
  return (
    <section aria-labelledby="progress-heading" className="border rounded-lg">
      <div className="px-4 py-3 border-b">
        <h2 id="progress-heading" className="text-sm font-medium text-foreground">Progress</h2>
      </div>
      <ol className="divide-y">
        {milestones.map((milestone, index) => {
          // A finished milestone that later progress has already moved past is
          // settled: it must stop advertising what it is "waiting on" and what
          // it estimates will happen next. Without this, a formation whose
          // certificate is already issued still reads "Filed — Done" alongside
          // "Waiting on: WY's review".
          const supersededByLaterProgress = milestones.slice(index + 1).some((later) => later.done)
          const description = describeMilestone(milestone, entity, supersededByLaterProgress)
          return (
            <li key={milestone.key} className="px-4 py-4">
              <div className="flex items-start gap-3">
                <div className="pt-0.5 shrink-0">
                  <StatusIcon status={milestone.status} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className="text-sm font-medium text-foreground">{MILESTONE_LABELS[milestone.key]}</span>
                    <span className={`text-xs font-medium ${STATUS_TEXT_CLASS[milestone.status]}`}>{STATUS_LABELS[milestone.status]}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{description.body}</p>
                  {description.waitingOn && (
                    <p className="text-xs text-muted-foreground/80 mt-1">Waiting on: {description.waitingOn}</p>
                  )}

                  {milestone.key === 'filed' && milestone.rejected && (
                    <div role="alert" className="mt-2 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2">
                      <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" aria-hidden="true" />
                      <p className="text-xs text-destructive">
                        <span className="font-medium">The state did not accept this filing.</span>{' '}
                        {milestone.rejection_reason || 'We are working on a resolution.'} Corppy is handling this — nothing is needed from you right now.
                      </p>
                    </div>
                  )}

                  {milestone.key === 'ein_issued' && taxRegistrationProgress.length > 0 && (
                    <ul className="mt-3 space-y-1.5 border-t pt-3">
                      {taxRegistrationProgress.map((registration) => (
                        <li key={registration.code} className="flex items-center justify-between gap-3 text-xs">
                          <span className="flex items-center gap-1.5 text-foreground">
                            {registration.completed
                              ? <CheckCircle2 className="w-3 h-3 text-green-600" aria-hidden="true" />
                              : <Circle className="w-3 h-3 text-muted-foreground/40" aria-hidden="true" />}
                            {registration.name}
                          </span>
                          <span className="text-muted-foreground">
                            {registration.completed ? `Complete${registration.completed_at ? ` · ${formatDate(registration.completed_at)}` : ''}` : `Pending · ${formatCurrency(registration.total_fee_cents)}`}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </li>
          )
        })}
      </ol>
    </section>
  )
}
